import React, { useEffect, useState } from "react";

import { changeForDB, getPlainTextLength } from "../../helpers/editorFunc";
import MySnackbar from "../../components/MySnackbar";
import { postMailing } from "../../http/mailingAPI";
import useSnackbar from "../../hooks/useSnackbar";
import Editor from "../../components/Editor";
import { tags } from "../../utils/tags";

import {
    Box,
    Button,
    Checkbox,
    Container,
    FormControlLabel,
    TextField,
    Typography,
} from "@mui/material";
import TagsModal from "../tagsModal";
import { LoadingButton } from "@mui/lab";

const MailingCreator = () => {
    const [message, setMessage] = useState("");
    const [points, setPoints] = useState({ from: 50, to: 50 });
    const [givePoints, setGivePoints] = useState(false);
    const [toSpecificPatients, setToSpecificPatients] = useState(false);
    const [image, setImage] = useState(null);
    const [mailingImage, setMailingImage] = useState(null);
    const [patientsFile, setPatientsFile] = useState(null);
    const [open, setOpen] = useState(false);
    const snack = useSnackbar(false, "Ошибка");
    const [pointsValidate, setPointsValidate] = useState(false);
    const [copiedTag, setCopiedTag] = useState("");
    const [isPublishing, setPublishing] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const messageLength = getPlainTextLength(message || "");

    // Очистка Object URL при размонтировании
    useEffect(() => {
        return () => {
            if (image) URL.revokeObjectURL(image);
        };
    }, [image]);

    const handleClose = () => setOpen(false);
    const handleOpen = () => setOpen(true);

    const handleChangeImage = (event) => {
        handleFile(event, setImage, setMailingImage, ["image/*"]);
    };

    const handleChangeFile = (event) => {
        handleFile(
            event,
            null,
            setPatientsFile,
            [".xlsx", ".xls"],
            "Недопустимый тип файла. Разрешены только .xlsx и .xls",
        );
    };

    function handleFile(
        event,
        urlSetter,
        fileSetter,
        allowedTypes = [],
        typeErrorMsg = "Недопустимый тип файла",
    ) {
        const maxSize = 3145728; // 3 MB
        const file = event.target.files[0];

        if (!file) {
            if (urlSetter)
                urlSetter((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return null;
                });
            fileSetter(null);
            return;
        }

        // Проверка типа файла
        if (allowedTypes.length > 0) {
            const typeMatches = allowedTypes.some((type) => {
                if (type.startsWith(".")) {
                    return file.name.toLowerCase().endsWith(type);
                }
                return file.type.match(type);
            });

            if (!typeMatches) {
                snack.handleChange(true, typeErrorMsg);
                fileSetter(null);
                return;
            }
        }

        if (file.size >= maxSize) {
            snack.handleChange(true, "Размер файла должен быть менее 3 мб");
            fileSetter(null);
            return;
        }

        if (urlSetter) {
            urlSetter((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return URL.createObjectURL(file);
            });
        }
        fileSetter(file);
    }

    async function save() {
        try {
            setPublishing(true);
            const formData = new FormData();
            formData.append("message", changeForDB(message));
            formData.append("givePoints", givePoints);
            formData.append("toAllPatients", !toSpecificPatients);

            if (givePoints) {
                formData.append("pointsFrom", points.from);
                formData.append("pointsTo", points.to);
            }

            if (mailingImage) formData.append("mailingImage", mailingImage);
            if (patientsFile) formData.append("patientsFile", patientsFile);

            // Сброс состояния
            setMessage("");
            setPoints({ from: 50, to: 50 });
            setGivePoints(false);
            setToSpecificPatients(false);
            setImage(null);
            setMailingImage(null);
            setPatientsFile(null);
            setPointsValidate(false);

            await postMailing(formData);
            setPublishing(false);
            snack.handleChange(true, "Рассылка начата!", "success");
        } catch (error) {
            snack.handleChange(true, error.message, "error");
            setPublishing(false);
            // snack.handleChange(
            //   true,
            //   error.response?.data?.message || 'Ошибка при отправке рассылки',
            //   'error'
            // );
        }
    }

    const handleCopyTag = (tag) => {
        navigator.clipboard.writeText(tag);
        setCopiedTag(tag);
        setSnackbarOpen(true);
        setTimeout(() => setSnackbarOpen(false), 3000);
    };

    return (
        <Container disableGutters sx={{ p: 3, maxWidth: "100%" }}>
            <MySnackbar {...snack} />

            <Box sx={{ my: 2, p: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                    }}
                >
                    <Typography variant="h4" component="h1" sx={{ m: 0 }}>
                        Создать рассылку
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleOpen}
                        size="small"
                    >
                        Теги
                    </Button>
                </Box>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    Текст рассылки
                </Typography>

                <Editor
                    value={message}
                    setValue={setMessage}
                    maxLength={800}
                    onLimit={() =>
                        snack.handleChange(
                            true,
                            "Кол-во символов превышает 800",
                        )
                    }
                />

                {image && (
                    <Box mb={2}>
                        <img
                            src={image}
                            alt="Preview"
                            style={{ maxWidth: "100%" }}
                        />
                    </Box>
                )}

                <Button variant="outlined" component="label" sx={{ mb: 3 }}>
                    Выбрать фото
                    <input
                        hidden
                        accept="image/*"
                        type="file"
                        onChange={handleChangeImage}
                    />
                </Button>

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={givePoints}
                                onChange={(event) => {
                                    setGivePoints(event.target.checked);
                                    if (!event.target.checked) {
                                        setPointsValidate(false);
                                    }
                                }}
                            />
                        }
                        label="Начислить баллы во время рассылки"
                    />
                </Box>

                {givePoints && (
                    <Box mb={2}>
                        <Typography mb={2}>
                            Количество начисляемых баллов
                        </Typography>
                        <Box>
                            <TextField
                                sx={{ mr: 2 }}
                                value={points.from}
                                error={pointsValidate}
                                onChange={(e) => {
                                    const value = +e.target.value;
                                    setPoints({ ...points, from: value });
                                    setPointsValidate(value > points.to);
                                }}
                                label="От"
                            />

                            <TextField
                                value={points.to}
                                error={pointsValidate}
                                onChange={(e) => {
                                    const value = +e.target.value;
                                    setPoints({ ...points, to: value });
                                    setPointsValidate(value < points.from);
                                }}
                                label="До"
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1 }}
                        >
                            Для случайного количества баллов: укажите диапазон
                            «от … до …» (например, от 10 до 20).
                            <br />
                            Для фиксированного количества баллов: укажите
                            одинаковые значения (например, от 10 до 10).
                        </Typography>
                    </Box>
                )}

                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={toSpecificPatients}
                                onChange={(event) =>
                                    setToSpecificPatients(event.target.checked)
                                }
                            />
                        }
                        label="Рассылка определенным пользователям"
                    />
                </Box>

                {toSpecificPatients && (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                                mb: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/images/patientFileExample.png"
                                    alt="Patient File Example"
                                    sx={{
                                        width: 121,
                                        height: 121,
                                        objectFit: "contain",
                                    }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{ textAlign: "center" }}
                                >
                                    Пример таблицы
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{
                                        width: 121,
                                        height: 121,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    Выбрать файл рассылки
                                    <input
                                        hidden
                                        accept=".xlsx, .xls"
                                        type="file"
                                        onChange={handleChangeFile}
                                    />
                                </Button>
                                <Typography
                                    variant="caption"
                                    sx={{ textAlign: "center" }}
                                >
                                    {patientsFile?.name}{" "}
                                    {patientsFile
                                        ? `${Math.floor(patientsFile.size / 1024)} Кб`
                                        : ""}
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}

                <Box>
                    <LoadingButton
                        variant="contained"
                        loading={isPublishing}
                        disabled={
                            messageLength === 0 ||
                            messageLength > 800 ||
                            pointsValidate ||
                            (toSpecificPatients && !patientsFile)
                        }
                        sx={{ mb: 1 }}
                        onClick={save}
                        disableElevation={true}
                    >
                        Отправить рассылку
                    </LoadingButton>
                </Box>
            </Box>

            <TagsModal
                open={open}
                onClose={handleClose}
                tags={tags}
                onCopyTag={handleCopyTag}
                copiedTag={copiedTag}
                snackbarOpen={snackbarOpen}
                onSnackbarClose={() => setSnackbarOpen(false)}
            />
        </Container>
    );
};

export default MailingCreator;
